import type { UIViewInjectedProps } from '@uirouter/react';
import { Button, PageHeader } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { Table } from '@/components/table';
import { getGenericFilters, toQueryFormat } from '@/components/table/utils';
import { boardingImportStore } from '@/stores';
import { showError } from '@/utils/common';
import { getTranslatedString } from '@/utils/intl';

import { IntakeStatus } from './intake-status';
import { SiReviewDrawer } from './review-drawer';
import { columns, getColumns, isActive, type SiImportRow } from './setup';

const POLL_INTERVAL_MS = 5000;
// Sticky filters that ride along with every list query. The page is
// dedicated to shipping_instruction imports — waybill_pdf rows have their
// own page. There is no upload UI here on purpose: SI intake is email-only
// (plus a curl-level admin endpoint), see the workspace SI feature plan.
const stickyFilters: cyrian.api.documentImports.GetParams = {
  type: ['shipping_instruction'],
};

export const BoardingImports = observer(({ transition }: UIViewInjectedProps) => {
  const [loading, setLoading] = useState(false);
  // Currently-open Review drawer target — just the row id. The live row is
  // derived from the store on every render so a refetch (e.g. after a failed
  // approve) flows reactively into the drawer through MobX's `observer`.
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  // Id of the row whose force-reparse POST is in flight. Renders a loading
  // spinner on the per-row button and disables Popconfirm so a double click
  // can't fire a second POST before fetchList settles into the new pending row.
  const [forceReparsingId, setForceReparsingId] = useState<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Cancel flag for the polling chain: cleared on unmount, checked inside the
  // async timer callback before scheduling the next tick or touching state —
  // otherwise an in-flight fetch resolving after unmount re-arms the timer
  // forever and calls setState on a torn-down component.
  const cancelledRef = useRef(false);

  const genericFilters = useMemo(() => getGenericFilters(columns), []);

  // Last list query actually sent (page/sort + sticky type). Refetches must
  // reuse it: a bare-stickyFilters refresh from table page ≥2 would fill the
  // store with page-1 rows, and the Review drawer — which derives its row
  // from the store by id — would silently close mid-edit.
  const lastParamsRef = useRef<cyrian.api.documentImports.GetParams>(stickyFilters);

  // `silent` skips the loading spinner — used for background polling so the
  // table doesn't flash a spinner every 5 s. Silent polls also bypass the
  // store's forceLoad, whose baked-in `.catch(showError)` would pop a toast
  // for every transient poll error that the next tick usually clears.
  const fetchList = useCallback(async (silent = false) => {
    if (silent) {
      try {
        const res = await api.documentImports.getAll(lastParamsRef.current).source;
        if (res?.data) {
          boardingImportStore.fillStore(res.data.items, res.data.count);
        }
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: silent background poll — a toast per transient error would spam the operator
        console.warn('[boarding-imports] silent poll failed, will retry', err);
      }
      return;
    }
    // User-initiated load — keep the store's built-in toast on error.
    setLoading(true);
    try {
      await boardingImportStore.forceLoad(lastParamsRef.current);
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  const onChangeTable = useCallback(
    (...args: Parameters<typeof toQueryFormat> extends [unknown, ...infer R] ? R : never[]) => {
      const params = (toQueryFormat as any).apply(null, [genericFilters, ...args]);
      lastParamsRef.current = {
        ...stickyFilters,
        ...params,
      } as cyrian.api.documentImports.GetParams;
      boardingImportStore.forceLoad(lastParamsRef.current);
    },
    [genericFilters],
  );

  // Lightweight polling: refresh silently while there are still active jobs.
  const schedulePoll = useCallback(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = setTimeout(async () => {
      if (cancelledRef.current) return;
      if (boardingImportStore.items.some((i) => isActive(i.status))) {
        await fetchList(true);
      }
      if (cancelledRef.current) return;
      schedulePoll();
    }, POLL_INTERVAL_MS);
  }, [fetchList]);

  useEffect(() => {
    cancelledRef.current = false;
    void fetchList();
    schedulePoll();
    return () => {
      cancelledRef.current = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [fetchList, schedulePoll]);

  const onReview = useCallback((row: SiImportRow) => {
    setReviewingId(row.id);
  }, []);

  // Force re-parse inserts a fresh pending row that takes over the source
  // document; the clicked row is kept, re-pointed at it and refreshed once the
  // new parse is approved. Close the Review drawer if it was open on it — the
  // row no longer owns the document, so its approve is refused and points at
  // the new import.
  const onForceReparse = useCallback(
    async (id: number) => {
      setForceReparsingId(id);
      try {
        await api.documentImports.forceReparse(id).source;
        if (reviewingId === id) setReviewingId(null);
        // Await the refresh so the per-row spinner stays up until the new
        // pending row appears in the store — until then nothing in the list
        // signals the click went through and an operator could fire a second one.
        await fetchList(true);
      } catch (err) {
        showError(err);
      } finally {
        setForceReparsingId(null);
      }
    },
    [fetchList, reviewingId],
  );

  // Derive the live row on each render so refetches and silent polls keep
  // the drawer's `importRow` prop fresh without manual sync.
  const reviewingRow =
    reviewingId != null ? (boardingImportStore.items.find((i) => i.id === reviewingId) ?? null) : null;

  const tableColumns = useMemo(
    () => getColumns({ onReview, onForceReparse, forceReparsingId }),
    [onReview, onForceReparse, forceReparsingId],
  );

  return (
    <>
      <PageHeader
        title={getTranslatedString('boardings.imports.title')}
        subTitle={getTranslatedString('boardings.imports.subtitle')}
        onBack={() => transition.router.stateService.go('base-layout.boardings')}
        style={{ padding: 0, marginBottom: 16 }}
        extra={[
          <Button key="refresh" onClick={() => void fetchList()} loading={loading}>
            <FormattedMessage id="boardings.imports.refresh" />
          </Button>,
        ]}
      />

      <IntakeStatus />

      <Table
        rowKey={(r: SiImportRow) => String(r.id)}
        columns={tableColumns}
        // Inline spread (not useMemo): store.items is an observable MobX
        // array with stable identity across in-place mutations — useMemo
        // with that dep would never recompute even when items change.
        dataSource={[...boardingImportStore.items]}
        store={boardingImportStore}
        loading={loading}
        onChange={onChangeTable}
        size="middle"
      />

      <SiReviewDrawer
        importRow={reviewingRow}
        onClose={() => setReviewingId(null)}
        onSubmitted={() => void fetchList(true)}
        onRefresh={() => fetchList(true)}
      />
    </>
  );
});
